import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { legalUserActions } from "./legalUser.actions";

export const LEGAL_USER_FEATURE_KEY = "legalUser";

export interface EmailData {
  uid: string;
  email: string;
  isPrimary: boolean;
  isVerified: boolean;
}

export interface PhoneNumber {
  uid: string;
  countryCode: string;
  lineNumber: string;
  merged: string;
  isPrimary: boolean;
  isVerified: boolean;
  verifiedAt: string;
}

export interface Address {
  uid: string;
  type: string;
  country: string;
  county: string;
  city: string;
  zipCode: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
}

export interface Language {
  code: string;
}

export interface ActUserIdProc {
  code: string;
  state: string;
}

export interface SignatureChain {
  actorShareCode: string;
  id: string;
  ph: string;
  sig: string;
  sigs: string[];
}

export interface IdentificationResultResult {
  token: string;
  email: string;
  locale: string;
  country: string;
}

export interface IdentificationResult {
  context: string | null;
  responder: string;
  result: IdentificationResultResult;
}

export interface IdentificationData {
  uid: string;
  state: string;
  signatureChain: SignatureChain;
  result: IdentificationResult;
  updatedBy: string;
  createdAt: number;
}

export interface UserDetails {
  registrationId: string;
  name: {
    fullName: string;
    shortName: string;
  };
  documents: any[];
  registrationNumber: string;
  taxIdNumber: string;
  referrerCode: string;
  refereeCode: string;
  distributorCode: string;
}

export interface LegalUserRegistrationData {
  code: string;
  type: string;
  subtype: string;
  method: string;
  submethod: string;
  displayText: string;
  isCompany: boolean;
  email: EmailData;
  discriminator: string;
  missions: string[];
  language: Language;
  state: string;
  actUserIdProc: ActUserIdProc;
  details: UserDetails;
  emails: EmailData[];
  addresses: Address[];
  phoneNumbers: PhoneNumber[];
}

export interface LegalUserState {
  registrationData: LegalUserRegistrationData | null;
  identificationData: IdentificationData | null;
  loading: boolean;
  error: string | null;
  identificationLoading: boolean;
  identificationError: string | null;
}

export const initialLegalUserState: LegalUserState = {
  registrationData: null,
  identificationData: null,
  loading: true,
  error: null,
  identificationLoading: false,
  identificationError: null,
};

export const legalUserSlice = createSlice({
  name: LEGAL_USER_FEATURE_KEY,
  initialState: initialLegalUserState,
  reducers: {
    clearLegalUserState: (state: LegalUserState) => {
      state.registrationData = null;
      state.identificationData = null;
      state.loading = true;
      state.error = null;
      state.identificationLoading = true;
      state.identificationError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(legalUserActions.fetchLegalUserRegistration.pending, (state: LegalUserState) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(legalUserActions.fetchLegalUserRegistration.fulfilled, (state: LegalUserState, action: PayloadAction<LegalUserRegistrationData>) => {
      state.loading = false;
      state.registrationData = action.payload;
    });
    builder.addCase(legalUserActions.fetchLegalUserRegistration.rejected, (state: LegalUserState, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch legal user registration data";
    });
    builder.addCase(legalUserActions.initiateIdentification.pending, (state: LegalUserState) => {
      state.identificationLoading = true;
      state.identificationError = null;
    });
    builder.addCase(legalUserActions.initiateIdentification.fulfilled, (state: LegalUserState, action: PayloadAction<IdentificationData>) => {
      state.identificationLoading = false;
      state.identificationData = action.payload;
    });
    builder.addCase(legalUserActions.initiateIdentification.rejected, (state: LegalUserState, action) => {
      state.identificationLoading = false;
      state.identificationError = (action.payload as string) || action.error.message || "Failed to initiate identification";
    });
  },
});

export const legalUserReducer = legalUserSlice.reducer;